/**
 * Lightweight in-app error telemetry.
 * - Captures window errors + unhandled promise rejections
 * - Rate-limits (max 20 events / 60s) so a runaway loop can't spam
 * - Deduplicates identical messages within a short window
 * - Logs to console with a stable tag; exposes queue for optional shipping later
 *
 * Intentionally has zero external dependencies (Sentry was removed by request).
 */

export type TelemetryEvent = {
  ts: number;
  kind: "error" | "unhandledrejection" | "manual";
  message: string;
  stack?: string;
  source?: string;
  lineno?: number;
  colno?: number;
  url: string;
};

const TAG = "[telemetry]";
const MAX_QUEUE = 50;
const RATE_LIMIT = 20;
const RATE_WINDOW_MS = 60_000;
const DEDUPE_MS = 5_000;

const queue: TelemetryEvent[] = [];
const recent = new Map<string, number>();
let windowStart = Date.now();
let windowCount = 0;
let installed = false;

function shouldAccept(message: string): boolean {
  const now = Date.now();
  if (now - windowStart > RATE_WINDOW_MS) {
    windowStart = now;
    windowCount = 0;
  }
  if (windowCount >= RATE_LIMIT) return false;
  const last = recent.get(message);
  if (last && now - last < DEDUPE_MS) return false;
  recent.set(message, now);
  windowCount++;
  return true;
}

export function reportEvent(evt: Omit<TelemetryEvent, "ts" | "url">) {
  if (!shouldAccept(evt.message)) return;
  const full: TelemetryEvent = {
    ...evt,
    ts: Date.now(),
    url: typeof window !== "undefined" ? window.location.href : "",
  };
  queue.push(full);
  if (queue.length > MAX_QUEUE) queue.shift();
  // eslint-disable-next-line no-console
  console.warn(TAG, full.kind, full.message, full.stack ? `\n${full.stack}` : "");
  // Fire-and-forget ship to Supabase. Never blocks or throws.
  void shipToSupabase(full);
}

/**
 * Best-effort insert into public.error_telemetry_events. Silent on failure
 * (network, RLS, offline) — telemetry must never break the app.
 */
async function shipToSupabase(evt: TelemetryEvent): Promise<void> {
  try {
    // Dynamic import so the telemetry lib stays dep-free at module init
    const { supabase } = await import("@/integrations/supabase/client");
    const { data: authData } = await supabase.auth.getSession();
    await supabase.from("error_telemetry_events").insert({
      ts: new Date(evt.ts).toISOString(),
      kind: evt.kind,
      message: evt.message.slice(0, 2000),
      stack: evt.stack ? evt.stack.slice(0, 8000) : null,
      source: evt.source ?? null,
      lineno: evt.lineno ?? null,
      colno: evt.colno ?? null,
      url: evt.url,
      user_id: authData.session?.user?.id ?? null,
      user_agent:
        typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 500) : null,
    });
  } catch {
    // swallow — telemetry is best-effort
  }
}

export function getTelemetryQueue(): readonly TelemetryEvent[] {
  return queue.slice();
}

export function installErrorTelemetry() {
  if (installed || typeof window === "undefined") return;
  installed = true;

  window.addEventListener("error", (e) => {
    reportEvent({
      kind: "error",
      message: e.message || "Unknown error",
      stack: e.error?.stack,
      source: e.filename,
      lineno: e.lineno,
      colno: e.colno,
    });
  });

  window.addEventListener("unhandledrejection", (e) => {
    const reason: unknown = e.reason;
    const message =
      reason instanceof Error
        ? reason.message
        : typeof reason === "string"
        ? reason
        : "Unhandled promise rejection";
    reportEvent({
      kind: "unhandledrejection",
      message,
      stack: reason instanceof Error ? reason.stack : undefined,
    });
  });

  // Expose read-only accessor for debugging in DevTools without leaking mutation.
  (window as unknown as { __geniebotTelemetry?: () => readonly TelemetryEvent[] }).__geniebotTelemetry =
    getTelemetryQueue;
}
